import BadgeDesignTab from '@/components/badge_printing/BadgeDesignTab'
import BadgeScanPrintTab from '@/components/badge_printing/BadgeScanPrintTab'
import BadgeSettingTab from '@/components/badge_printing/BadgeSettingTab'
import EventLayout from '@/components/layouts/EventLayout'
import TabbedPage from '@/components/shared/TabbedPage'
import { TabIconLabel } from '@/components/shared/TabIconLabel'
import type {
  BadgeMergeTag,
  BadgePrintEventData,
  BadgePrintRegistrant,
  BadgeTemplate,
} from '@/types/badgeDesigner'
import { Head } from '@inertiajs/react'
import { useCallback, useMemo, useState } from 'react'
import { ScanOutlined } from '@ant-design/icons'
import { HiOutlineCog } from 'react-icons/hi'
import { PiIdentificationBadgeLight } from 'react-icons/pi'

export type BadgePrintingPageProps = {
  eventId: number
  eventName: string
  eventSlug: string
  eventData?: BadgePrintEventData
  badgeSetting?: {
    is_badge_printing_enabled: boolean
    is_print_triggers_check_in_enabled: boolean
  }
  badgeTemplate?: BadgeTemplate
  mergeTags?: BadgeMergeTag[]
  registrantsForTest?: BadgePrintRegistrant[]
}

const BADGE_PRINTING_TAB_KEYS = ['scan-print', 'badge-design', 'settings']

const getTabFromUrl = () => {
  if (typeof window === 'undefined') return 'scan-print'

  const tab = new URLSearchParams(window.location.search).get('tab')
  return tab && BADGE_PRINTING_TAB_KEYS.includes(tab) ? tab : 'scan-print'
}

export default function BadgePrintingPage({
  eventName,
  eventSlug,
  eventData,
  badgeSetting,
  badgeTemplate,
  mergeTags = [],
  registrantsForTest = [],
}: BadgePrintingPageProps) {
  const [activeTabKey, setActiveTabKey] = useState(getTabFromUrl)

  const handleTabChange = useCallback((key: string) => {
    setActiveTabKey(key)

    if (typeof window === 'undefined') return

    const url = new URL(window.location.href)
    url.searchParams.set('tab', key)
    window.history.replaceState({}, '', url.toString())
  }, [])

  const handleOpenSettings = useCallback(() => {
    handleTabChange('settings')
  }, [handleTabChange])

  const tabs = useMemo(
    () => [
      {
        key: 'scan-print',
        label: <TabIconLabel icon={<ScanOutlined />} label="Scan & Print" />,
        children: (
          <BadgeScanPrintTab
            eventSlug={eventSlug}
            eventData={eventData}
            badgeTemplate={badgeTemplate}
            isBadgePrintingEnabled={badgeSetting?.is_badge_printing_enabled ?? false}
            onOpenSettings={handleOpenSettings}
          />
        ),
      },
      {
        key: 'badge-design',
        label: <TabIconLabel icon={<PiIdentificationBadgeLight />} label="Badge Design" />,
        children: (
          <BadgeDesignTab
            eventSlug={eventSlug}
            eventData={eventData}
            badgeTemplate={badgeTemplate}
            mergeTags={mergeTags}
            registrantsForTest={registrantsForTest}
          />
        ),
      },
      {
        key: 'settings',
        label: <TabIconLabel icon={<HiOutlineCog />} label="Settings" />,
        children: <BadgeSettingTab />,
      },
    ],
    [
      eventData,
      eventSlug,
      badgeSetting,
      badgeTemplate,
      mergeTags,
      registrantsForTest,
      handleOpenSettings,
    ],
  )

  return (
    <EventLayout>
      <Head title={`${eventName} - Badge Printing`} />
      <TabbedPage
        title="Badge Printing"
        subtitle="Configure badge printing and attendee check-in options for your event"
        tabs={tabs}
        activeKey={activeTabKey}
        onTabChange={handleTabChange}
        showTabContainer={false}
        containerClassName="p-4"
      />
    </EventLayout>
  )
}
